import type { IntegrationCredential } from '@evogard/contract';
import type { Tone } from '@evogard/domain';

/**
 * A situação de uma integração numa palavra, para a lista da tela de
 * integrações (16/10/2026).
 *
 * Ela é DERIVADA do que a tela já lê — a credencial, o último teste e o que
 * cada integração ainda pede (`missing`) — e nunca afirma mais do que isso:
 * "Conexão OK" só aparece depois de um teste que deu certo, e uma credencial
 * guardada sem teste é só "Configurada".
 *
 * A ordem é a da gravidade: o que impede de funcionar vem antes do que falta
 * completar, e o que falta completar vem antes do que está bem.
 */
export interface IntegrationState {
  tone: Extract<Tone, 'sucesso' | 'alerta' | 'erro' | 'neutro'>;
  label: string;
}

export function integrationState(
  integration: IntegrationCredential,
  /** O passo que esta integração ainda pede além da credencial — "Falta o webhook". */
  missing: string | null = null,
): IntegrationState {
  if (integration.unreadable) return { tone: 'erro', label: 'Credencial não abre' };
  if (!integration.configured) return { tone: 'neutro', label: 'Sem credencial' };
  if (integration.verifiedOk === false) return { tone: 'erro', label: 'Conexão falhou' };
  if (missing) return { tone: 'alerta', label: missing };
  if (integration.verifiedOk === true) return { tone: 'sucesso', label: 'Conexão OK' };
  return { tone: 'neutro', label: 'Configurada' };
}

/**
 * O grupo de cada integração na lista — pelo que ela faz na casa, e não pelo
 * fornecedor. Integração nova sem grupo cai em "Outras", e aparece mesmo assim.
 */
export const INTEGRATION_GROUPS: readonly {
  label: string;
  integrations: readonly string[];
}[] = [
  { label: 'Cadastro e vendas', integrations: ['hinova', 'oryon'] },
  { label: 'Pagamentos', integrations: ['gateway'] },
  { label: 'Financeiro', integrations: ['contaazul'] },
  { label: 'Mensagens', integrations: ['opasuite', 'sendgrid'] },
  { label: 'Sinistros', integrations: ['caf'] },
  { label: 'Assistência 24h', integrations: ['googlemaps'] },
];

/** A linha de apoio de cada integração na lista: o que ela traz para a casa. */
export const INTEGRATION_PURPOSES: Readonly<Record<string, string>> = {
  hinova: 'Associados, veículos e boletos',
  oryon: 'Vendas',
  gateway: 'Cobranças',
  contaazul: 'Contas da empresa',
  opasuite: 'WhatsApp',
  caf: 'Documentos do associado',
  googlemaps: 'Distâncias e rotas',
  sendgrid: 'Confirmação de conta',
};
